import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@dopomogai/ui';

type HeaderMode = 'homepage' | 'content';

type Props = {
  mode?: HeaderMode;
  showPreviewBadge?: boolean;
};

const navItems = [
  { label: 'Software Services', href: '/software-services' },
  { label: 'Insights', href: '/blog' },
  { label: 'Docs', href: '/docs/concierge-embed' },
] as const;

export function WebsiteV2Header({ mode = 'homepage', showPreviewBadge = false }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const ctaLabel = mode === 'homepage' ? 'For Software-Services Firms' : 'Apply for the AI Revenue Audit';
  const ctaHref = mode === 'homepage' ? '/software-services' : '/ai-revenue-audit';

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/90 bg-[#fffdf8]/95 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 md:px-8">
        <div className="flex items-center gap-3">
          <Link to="/" className="text-sm font-black uppercase tracking-[0.28em] text-slate-950">
            Dopomogai
          </Link>
          {showPreviewBadge ? (
            <span className="hidden rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-700 md:inline-flex">
              V2 Preview
            </span>
          ) : null}
        </div>

        <nav className="hidden items-center gap-6 md:flex">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className="text-sm font-bold text-slate-600 transition-colors hover:text-slate-950"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex">
          <Button size="sm" text={ctaLabel} onClick={() => navigate(ctaHref)} />
        </div>

        <button
          type="button"
          onClick={() => setIsOpen((current) => !current)}
          className="inline-flex items-center justify-center rounded-xl border-2 border-black bg-white p-2 text-slate-900 shadow-[3px_3px_0px_0px_rgba(15,23,42,1)] md:hidden"
          aria-label={isOpen ? 'Close navigation' : 'Open navigation'}
        >
          {isOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {isOpen ? (
        <div className="border-t border-slate-200 bg-[#fffdf8] px-4 py-4 md:hidden">
          <div className="mx-auto flex max-w-6xl flex-col gap-3">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setIsOpen(false)}
                className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700"
              >
                {item.label}
              </Link>
            ))}
            <Button
              text={ctaLabel}
              onClick={() => {
                setIsOpen(false);
                navigate(ctaHref);
              }}
            />
          </div>
        </div>
      ) : null}
    </header>
  );
}

export default WebsiteV2Header;
